import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, Loader2 } from 'lucide-react';
import PatientPortalShell from '@/modules/referral/components/PatientPortalShell';
import { patientPortalApi } from '../api/patientPortal.api';
import { formatDateIST } from '@/utils/date';
import { formatDurationMinutes } from '@/modules/reception/formatVisitDuration';
import { IITH, SERIF } from '@/pages/landing/iithBrand';

export default function PatientAppointmentsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    patientPortalApi.appointments()
      .then((d) => setItems(d?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PatientPortalShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#071B34]" style={{ fontFamily: SERIF }}>My appointments</h1>
          <p className="text-sm text-[#5A6B7C]">Track every campus visit for you and your family.</p>
        </div>
        <Link
          to="/patient/appointments/book"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"
          style={{ background: IITH.saffron }}
        >
          <CalendarPlus className="h-4 w-4" /> Book new
        </Link>
      </div>
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-[#F15A22]" /></div>
      ) : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-[#D5DEE8] py-12 text-center text-sm text-[#7A8B9C]">
          No appointments yet. Book your first OPD slot.
        </p>
      ) : (
        <ul className="space-y-2.5" data-testid="patient-appt-list">
          {items.map((a) => {
            const total = formatDurationMinutes(a.total_hospital_minutes);
            return (
              <li key={a.id} className="rounded-2xl border border-[#D5DEE8] bg-white px-4 py-3.5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-[#071B34]">
                      {a.date ? formatDateIST(a.date) : '—'}{a.time ? ` · ${a.time}` : ''}
                    </p>
                    <p className="text-[12px] text-[#5A6B7C]">{a.doctor_name} · {a.visit_type}</p>
                    {a.appointment_number ? (
                      <p className="text-[11px] font-mono text-[#9AADBE] mt-0.5">{a.appointment_number}</p>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-900">
                      {a.status_label || a.status}
                    </span>
                    {total ? (
                      <p className="text-[11px] text-[#5A6B7C] mt-1">
                        {total} in hospital{a.total_hospital_open ? ' …' : ''}
                      </p>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </PatientPortalShell>
  );
}
